import { calculateDmarcPrice } from '@/data/dmarc-products';
import { calculatePrice } from '@/data/products';
import { generateStandardCertId } from '@/utils/cert-utils';
import { getStripeQuantityFromEmailVolume } from '@/utils/dmarc-stripe-utils';
import { isSubscriptionProduct } from '@/utils/subscription-utils';
import { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-hot-toast';

const Context = createContext();

// Helper function to determine the CMC type from product ID
const getCMCType = (productId) => {
  if (productId === 'common-mark-certificate') {
    return 'Prior Use Mark';
  } else if (productId === 'modified-registered-mark') {
    return 'Modified Registered Mark';
  }
  return null;
};

export const StateContext = ({ children }) => {
  const [showCart, setShowCart] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalQuantities, setTotalQuantities] = useState(0);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      try {
        const savedCart = localStorage.getItem('cartItems');
        const savedPrice = localStorage.getItem('totalPrice');
        const savedQuantities = localStorage.getItem('totalQuantities');

        if (savedCart) setCartItems(JSON.parse(savedCart));
        if (savedPrice) setTotalPrice(parseFloat(savedPrice));
        if (savedQuantities) setTotalQuantities(parseInt(savedQuantities));
      } catch (error) {
        console.error('Error loading cart data:', error);
      } finally {
        setLoaded(true);
      }
    }
  }, []);

  useEffect(() => {
    if (loaded && typeof window !== 'undefined') {
      localStorage.setItem('cartItems', JSON.stringify(cartItems));
      localStorage.setItem('totalPrice', totalPrice.toString());
      localStorage.setItem('totalQuantities', totalQuantities.toString());
    }
  }, [cartItems, totalPrice, totalQuantities, loaded]);

  const onAdd = (product, quantity, options = {}) => {
    console.log('🔍 DEBUG: StateContext onAdd called with product:', {
      id: product.id,
      name: product.name,
      sourcePageType: product.sourcePageType,
      hasSourcePageType: !!product.sourcePageType,
      skipPriceCalculation: product.skipPriceCalculation,
      allKeys: Object.keys(product),
    });

    if (product.skipPriceCalculation && 'finalPrice' in product) {
      // Determine CMC type if applicable
      const cmcType = getCMCType(product.id);

      const newCartItem = {
        ...product,
        uniqueId: `${product.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        quantity,
        price: product.finalPrice,
        cmcType, // Add CMC type if available
      };

      console.log('🔍 DEBUG: StateContext created cart item (skipPriceCalculation path):', {
        id: newCartItem.id,
        sourcePageType: newCartItem.sourcePageType,
        hasSourcePageType: !!newCartItem.sourcePageType,
        allKeys: Object.keys(newCartItem),
      });

      setCartItems((prevItems) => [...prevItems, newCartItem]);
      setTotalPrice((prevTotal) => prevTotal + newCartItem.price);
      setTotalQuantities((prevQuantities) => prevQuantities + quantity);

      toast.success(`${product.name} added to cart`);
      return;
    }

    const { domain = 1, year = 1, logo = 1 } = options;
    const period = year;
    const domainCount = domain;
    const logoCount = logo;
    const isSubscription = isSubscriptionProduct(product);

    // Determine CMC type if applicable
    const cmcType = getCMCType(product.id);

    // FUNDAMENTAL CHANGE: Treat logo count differently
    // Instead of multiplying the price by logo count, we'll add multiple items to the cart
    // Each item represents a single logo certificate, but preserves the domain count
    const newCartItems = [];
    let totalAddedPrice = 0;

    // Add a separate cart item for each logo
    for (let i = 0; i < logoCount; i++) {
      // Initialize variables for pricing and IDs
      let price = 0;
      let basePrice = 0;
      let priceId = null;
      let productId = null;

      // Generate a unique ID for this specific cart item
      const uniqueId = `${product.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}-logo${i + 1}`;

      // Generate a unique certId for each logo using standardized format
      const uniqueCertId = generateStandardCertId(product.id);

      // For subscription products, try to get price and IDs from subscriptionOptions
      if (isSubscription && product.subscriptionOptions) {
        // Try numeric key first (backward compatibility)
        if (product.subscriptionOptions[period]) {
          const subscriptionOption = product.subscriptionOptions[period];
          basePrice = subscriptionOption.amount;
          priceId = subscriptionOption.priceId;
          productId = subscriptionOption.productId;

          // Apply ONLY domain multiplier to the base price (logo is now separate items)
          price = basePrice * domainCount;

          console.log(
            `Using subscription options with numeric key ${period} for ${product.id} (Logo ${i + 1} of ${logoCount})`,
            {
              basePrice,
              finalPrice: price,
              domainCount,
              multiplier: domainCount,
              priceId,
              productId,
            }
          );
        }
        // Try string key format (e.g. '1yr')
        else if (product.subscriptionOptions[`${period}yr`]) {
          const subscriptionOption = product.subscriptionOptions[`${period}yr`];
          basePrice = subscriptionOption.amount;
          priceId = subscriptionOption.priceId;
          productId = subscriptionOption.productId;

          // Apply ONLY domain multiplier to the base price (logo is now separate items)
          price = basePrice * domainCount;

          console.log(
            `Using subscription options with string key ${period}yr for ${product.id} (Logo ${i + 1} of ${logoCount})`,
            {
              basePrice,
              finalPrice: price,
              domainCount,
              multiplier: domainCount,
              priceId,
              productId,
            }
          );
        }
        // If we still don't have price info, log a warning
        else {
          console.warn(
            `⚠️ No subscription option found for ${product.id} with period ${period}. Available keys:`,
            Object.keys(product.subscriptionOptions)
          );
        }
      }

      // If we still don't have a price from subscription options, fall back to standard price calculation
      if (!price) {
        // Make a copy of options with logo count set to 1
        const singleLogoOptions = { ...options, logo: 1 };

        price =
          product.type === 'dmarc'
            ? calculateDmarcPrice(product, singleLogoOptions).total
            : calculatePrice(product, singleLogoOptions).total;

        console.log(
          `Using calculated price for ${product.id} (Logo ${i + 1} of ${logoCount}): ${price}`
        );
      }

      const newCartItem = {
        ...product,
        uniqueId,
        certId: uniqueCertId,
        quantity,
        options: { ...options, logo: 1 }, // Each item represents a single logo
        price,
        basePrice,
        isSubscription,
        priceId,
        productId,
        logoIndex: i + 1, // Add an index to track which logo this is
        logoCount, // Keep the total logo count for reference
        cmcType, // Add CMC type if available
      };

      newCartItems.push(newCartItem);
      totalAddedPrice += price;
    }

    // Add all the cart items
    setCartItems((prevItems) => [...prevItems, ...newCartItems]);
    setTotalPrice((prevTotal) => prevTotal + totalAddedPrice);
    setTotalQuantities((prevQuantities) => prevQuantities + quantity * logoCount);

    // Display success message
    if (logoCount === 1) {
      toast.success(`${product.name} added to cart`);
    } else {
      toast.success(`${logoCount} ${product.name} certificates added to cart`);
    }
  };

  const onAddDmarc = (product, quantity, selectedOption) => {
    try {
      // Get the selected email volume (tier)
      const emailVolume = selectedOption;

      // Find the product option for pricing
      const dmarcProduct = product.features
        ? product // Legacy format with features array
        : {
            ...product,
            features: [
              {
                name: 'Emails',
                options: product.options.map((opt) => ({ value: opt.emails, ...opt })),
              },
            ],
          };

      const emailFeature = dmarcProduct.features?.find((f) => f.name === 'Emails') || {};
      const emailOptions = emailFeature.options || [];
      const selectedOptionData =
        emailOptions.find((o) => o.value === emailVolume) || emailOptions[0];

      if (!selectedOptionData) {
        throw new Error(`No pricing option found for email volume: ${emailVolume}`);
      }

      // FIXED: Use the corrected utility function for Stripe quantity calculation
      const stripeQuantity = getStripeQuantityFromEmailVolume(emailVolume);

      // Create cart item with Stripe metadata (without calling utility functions)
      const uniqueId = `${product.id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

      const newCartItem = {
        ...product,
        uniqueId,
        quantity,
        selectedOption: emailVolume,
        price: selectedOptionData.annualPrice, // Use annual price for subscriptions

        // Stripe Integration Data (will be resolved server-side)
        type: 'dmarc',
        isSubscription: true,
        emailVolume,
        stripeQuantity,
        // Note: stripePriceId and stripeProductId will be resolved server-side
        // during checkout using the product.id and environment variables

        // Pricing breakdown
        monthlyPrice: selectedOptionData.price,
        annualPrice: selectedOptionData.annualPrice,
        msrp: selectedOptionData.msrp,

        // Product metadata for SetupIntent
        dmarcProductId: product.id,
        productType: 'dmarc',
      };

      setCartItems((prevItems) => [...prevItems, newCartItem]);
      setTotalPrice((prevTotal) => prevTotal + newCartItem.price);
      setTotalQuantities((prevQuantities) => prevQuantities + quantity);

      toast.success(`${product.name} added to cart`);

      console.log('DMARC product added to cart:', {
        productId: product.id,
        emailVolume,
        stripeQuantity,
        annualPrice: selectedOptionData.annualPrice,
        note: 'Stripe IDs will be resolved server-side during checkout',
      });
    } catch (error) {
      console.error('Error adding DMARC product to cart:', error);
      toast.error(`Failed to add ${product.name} to cart: ${error.message}`);
    }
  };

  const onRemove = (cartItem) => {
    const updatedCartItems = cartItems.filter((item) => item.uniqueId !== cartItem.uniqueId);

    setCartItems(updatedCartItems);
    setTotalPrice((prevTotal) => prevTotal - (cartItem?.price || 0));
    setTotalQuantities((prevQuantities) => prevQuantities - (cartItem?.quantity || 0));
  };

  const toggleCartItemQuantity = (id, value) => {
    const foundItem = cartItems.find((item) => item.uniqueId === id);
    const index = cartItems.findIndex((item) => item.uniqueId === id);

    if (!foundItem) return;

    const newCartItems = [...cartItems];

    if (value === 'inc') {
      newCartItems[index] = {
        ...foundItem,
        quantity: foundItem.quantity + 1,
      };
      setCartItems(newCartItems);
      setTotalPrice((prevTotal) => prevTotal + foundItem.price);
      setTotalQuantities((prevQty) => prevQty + 1);
    } else if (value === 'dec') {
      if (foundItem.quantity > 1) {
        newCartItems[index] = {
          ...foundItem,
          quantity: foundItem.quantity - 1,
        };
        setCartItems(newCartItems);
        setTotalPrice((prevTotal) => prevTotal - foundItem.price);
        setTotalQuantities((prevQty) => prevQty - 1);
      }
    }
  };

  const clearCart = () => {
    setCartItems([]);
    setTotalPrice(0);
    setTotalQuantities(0);

    // Clear localStorage
    if (typeof window !== 'undefined') {
      localStorage.removeItem('cartItems');
      localStorage.removeItem('totalPrice');
      localStorage.removeItem('totalQuantities');
    }
  };

  return (
    <Context.Provider
      value={{
        showCart,
        setShowCart,
        cartItems,
        totalPrice,
        totalQuantities,
        loaded,
        onAdd,
        onAddDmarc,
        onRemove,
        toggleCartItemQuantity,
        clearCart,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export const useStateContext = () => useContext(Context);
